import React, { useEffect, useState } from "react";
import OrderCard from "./component/Ordercard";
import "./RetailOrders.css";
import axiosInstance from "../../../../axiosInstance";
import Pagination from "../../../../components/pagination/Pagination";
import LoadingComponent from "../../../../components/loading/Loading";

interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  price: number;
  line_total: number;
  description: string;
  image?: string; // Add image if available
}

interface ShippingDetails {
  name: string;
  address: string;
  phone: string;
}

interface OrderStatus {
  status: string;
  date: string;
}

interface Order {
  id: string;
  amount: number;
  createdAt: string;
  order_items: OrderItem[];
  customer: {
    full_name: string;
    phone_number: string;
  };
  vendor: {
    name: string;
    address?: string;
    phone?: string;
  };
  status: string;
}

const ORDERS_PER_PAGE = 5;

const RetailOrders = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Pagination logic
  const totalPages = Math.ceil(orders.length / ORDERS_PER_PAGE);
  const paginatedOrders = orders.slice(
    (currentPage - 1) * ORDERS_PER_PAGE,
    currentPage * ORDERS_PER_PAGE
  );

  useEffect(() => {
    axiosInstance
      .get("/orders")
      .then((res) => {
        const sortedOrders = (res.data.data || []).sort(
          (a: Order, b: Order) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setOrders(sortedOrders);
      })
      .catch((err) => {
        console.error("Failed to fetch orders:", err);
      })
      .finally(() => setLoading(false));
  }, []);

  // Transform API order to OrderCard props
  const mapOrderToCard = (order: Order) => {
    // Format date helper
    const formatDate = (dateStr?: string) =>
      dateStr
        ? new Date(dateStr).toLocaleDateString(undefined, {
            year: "numeric",
            month: "long",
            day: "numeric",
          })
        : "pending";

    // Find actual dates for each status if available
    // You may need to adjust these if your API provides more timestamps
    const placedDate = formatDate(order.createdAt);
    // If your API has readyForPickupDate or completeDate, use them; else "pending"
    const readyForPickupDate =
      order.status === "ready_for_pickup" || order.status === "complete"
        ? placedDate // Replace with actual date if available
        : "pending";
    const completeDate =
      order.status === "complete"
        ? placedDate // Replace with actual date if available
        : "pending";

    return {
      orderId: order.id,
      amount: order.amount,
      orderDate: placedDate,
      items: order.order_items.length,
      shippingDetails: {
        name: order.customer.full_name,
        address: order.vendor.name,
        phone: order.customer.phone_number,
      },
      orderStatus: [
        { status: "Order placed", date: placedDate },
        { status: "Ready for Pickup", date: readyForPickupDate },
        { status: "Order Complete", date: completeDate },
      ],
      orderItems: order.order_items.map((item) => ({
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        image: item.image,
      })),
    };
  };

  if (loading) {
    return <LoadingComponent />;
  }

  return (
    <div className="retail-orders">
      <h3>Ongoing Orders</h3>
      {orders.length === 0 && <div>No orders found.</div>}
      {paginatedOrders.map((order) => (
        <OrderCard key={order.id} {...mapOrderToCard(order)} />
      ))}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default RetailOrders;
